import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Calendar, Clock, User, Mail, Phone, FileText } from 'lucide-react';
import { Button } from '@/components/ui/Button';

import { createManualBooking, getProviderServices, getAvailableSlots } from '@/services/bookingApi';
import { StaffPicker } from '@/components/booking/StaffPicker';
import { teamApi } from '@/services/teamApi';
import type { Service, ServiceCategory, TimeSlot } from '@/types';

interface ManualBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  profileId: string;
  providerTimezone?: string;
}

export const ManualBookingModal: React.FC<ManualBookingModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  profileId,
}) => {
  const { t } = useTranslation('booking');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Services
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [uncategorized, setUncategorized] = useState<Service[]>([]);
  
  // Form state
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerNotes, setCustomerNotes] = useState('');
  const [selectedServiceId, setSelectedServiceId] = useState<string | null>(null);
  const [bookingDate, setBookingDate] = useState<string>('');
  const [bookingTime, setBookingTime] = useState<string | null>(null);
  const [preConfirmed, setPreConfirmed] = useState(false);
  const [assignedUserId, setAssignedUserId] = useState<string | null>(null);
  const [teamId, setTeamId] = useState<string | undefined>(undefined);
  
  // Available slots
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  // Load services on mount
  useEffect(() => {
    if (isOpen) {
      loadServices();
      teamApi.getMyTeams().then((teams) => setTeamId(teams[0]?.id)).catch(() => setTeamId(undefined));
    }
  }, [isOpen]);

  // Load available slots when date or service changes
  useEffect(() => {
    if (isOpen && bookingDate && selectedServiceId) {
      loadAvailableSlots();
    }
  }, [isOpen, bookingDate, selectedServiceId]);

  const loadServices = async () => {
    try {
      const data = await getProviderServices();
      setCategories(data.categories);
      setUncategorized(data.uncategorized);
    } catch (err) {
      console.error('Failed to load services:', err);
    }
  };

  const loadAvailableSlots = async () => {
    if (!selectedServiceId || !bookingDate) return;
    
    setLoadingSlots(true);
    try {
      const response = await getAvailableSlots(profileId, bookingDate, [selectedServiceId]);
      // Filter to only available slots
      const available = response.slots?.filter((s: TimeSlot) => s.available) || [];
      setAvailableSlots(available);
      setBookingTime(null); // Reset time when slots change
    } catch (err) {
      console.error('Failed to load slots:', err);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedServiceId || !bookingDate || !bookingTime) {
      setError('Please select a service, date, and time');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await createManualBooking({
        customer_name: customerName,
        customer_email: customerEmail,
        customer_phone: customerPhone || undefined,
        service_ids: [selectedServiceId],
        booking_date: bookingDate,
        booking_time: bookingTime,
        customer_notes: customerNotes || undefined,
        pre_confirmed: preConfirmed,
        assigned_user_id: assignedUserId,
      });
      
      onSuccess();
      onClose();
      resetForm();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create booking');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setCustomerName('');
    setCustomerEmail('');
    setCustomerPhone('');
    setCustomerNotes('');
    setSelectedServiceId(null);
    setBookingDate('');
    setBookingTime(null);
    setPreConfirmed(false);
    setAssignedUserId(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Get minimum date (today)
  const minDate = new Date().toISOString().split('T')[0];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">{t('manualBooking.title')}</h2>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              {error}
            </div>
          )}

          {/* Customer Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('manualBooking.customerName')} *
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                required
                className="w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                placeholder="John Doe"
              />
            </div>
          </div>

          <StaffPicker
            teamId={teamId}
            value={assignedUserId}
            onChange={setAssignedUserId}
            allowAny
            label={t('manualBooking.assignStaff', { defaultValue: 'Assign staff' })}
          />

          {/* Customer Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('manualBooking.customerEmail')} *
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="email"
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
                required
                className="w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                placeholder="john@example.com"
              />
            </div>
          </div>

          {/* Customer Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('manualBooking.customerPhone')}
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="tel"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                placeholder="+1 234 567 8900"
              />
            </div>
          </div>

          {/* Service Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('manualBooking.service')} *
            </label>
            <select
              value={selectedServiceId || ''}
              onChange={(e) => setSelectedServiceId(e.target.value || null)}
              required
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
            >
              <option value="">{t('manualBooking.selectService')}</option>
              {categories.map((cat) => (
                <optgroup key={cat.id} label={cat.name}>
                  {cat.services.map((svc) => (
                    <option key={svc.id} value={svc.id}>
                      {svc.name} ({svc.duration_minutes} min)
                    </option>
                  ))}
                </optgroup>
              ))}
              {uncategorized.map((svc) => (
                <option key={svc.id} value={svc.id}>
                  {svc.name} ({svc.duration_minutes} min)
                </option>
              ))}
            </select>
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('manualBooking.date')} *
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="date"
                value={bookingDate}
                onChange={(e) => setBookingDate(e.target.value)}
                required
                min={minDate}
                className="w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>
          </div>

          {/* Time */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('manualBooking.time')} *
            </label>
            <div className="relative">
              <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <select
                value={bookingTime || ''}
                onChange={(e) => setBookingTime(e.target.value || null)}
                required
                disabled={!selectedServiceId || !bookingDate || loadingSlots}
                className="w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-gray-100"
              >
                <option value="">
                  {loadingSlots ? t('manualBooking.loadingSlots') : t('manualBooking.selectTime')}
                </option>
                {availableSlots.map((slot) => (
                  <option key={slot.time} value={slot.time}>
                    {slot.time}
                    {slot.pricing_type === 'peak' && ' 🔥'}
                    {slot.pricing_type === 'off_peak' && ' ❄️'}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Pre-confirmed */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-gray-700">{t('manualBooking.preConfirmed')}</p>
              <p className="text-sm text-gray-500">{t('manualBooking.preConfirmedHint')}</p>
            </div>
            <button
              type="button"
              onClick={() => setPreConfirmed(!preConfirmed)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                preConfirmed ? 'bg-green-500' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  preConfirmed ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('manualBooking.notes')}
            </label>
            <div className="relative">
              <FileText className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <textarea
                value={customerNotes}
                onChange={(e) => setCustomerNotes(e.target.value)}
                rows={3}
                className="w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary resize-none"
                placeholder={t('manualBooking.notesPlaceholder')}
              />
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={handleClose} className="flex-1">
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isLoading} className="flex-1">
              {isLoading ? t('common.loading') : t('manualBooking.create')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
